import { useEffect, useState } from "react";
import { Table, Tag, Switch, Progress, Spin } from "antd";
import moment from "moment";
import { Toast } from "utils";
import {
  getUserPurchases,
  updateEnrollmentStatus,
  updateModuleStatus,
} from "services/courseModules";
import { Container, Title } from "./styles";

const STATUS_COLORS = {
  CONCLUIDO: "green",
  ANDAMENTO: "blue",
  INSCRITO: "gold",
  ANALISE: "default",
};

export const UserPurchases = ({ idUser }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  // ids com toggle em andamento (chaves "curso-<id>" / "modulo-<id>")
  const [updatingKeys, setUpdatingKeys] = useState([]);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const { data, ok } = await getUserPurchases(idUser);
      if (ok) setPurchases(data || []);
    } catch (error) {
      console.error(error);
      Toast.showErrorMessage("Erro ao carregar os cursos comprados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idUser) fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUser]);

  const isUpdating = (key) => updatingKeys.includes(key);

  const setUpdating = (key, value) =>
    setUpdatingKeys((prev) =>
      value ? [...prev, key] : prev.filter((k) => k !== key)
    );

  const handleToggleEnrollment = async (curso, checked) => {
    const key = `curso-${curso.id}`;
    setUpdating(key, true);
    try {
      const { ok } = await updateEnrollmentStatus(curso.id, checked);
      if (ok) {
        setPurchases((prev) =>
          prev.map((c) =>
            c.id === curso.id ? { ...c, fg_ativo: checked } : c
          )
        );
        Toast.showSuccessMessage(
          checked ? "Inscrição reativada." : "Inscrição inativada."
        );
      } else {
        Toast.showErrorMessage("Não foi possível atualizar a inscrição.");
      }
    } catch (error) {
      console.error(error);
      Toast.showErrorMessage("Não foi possível atualizar a inscrição.");
    } finally {
      setUpdating(key, false);
    }
  };

  const handleToggleModule = async (cursoId, modulo, checked) => {
    const key = `modulo-${modulo.id}`;
    setUpdating(key, true);
    try {
      const { ok } = await updateModuleStatus(modulo.id, checked);
      if (ok) {
        setPurchases((prev) =>
          prev.map((c) =>
            c.curso_id === cursoId
              ? {
                  ...c,
                  modulos: c.modulos.map((m) =>
                    m.id === modulo.id ? { ...m, fg_ativo: checked } : m
                  ),
                }
              : c
          )
        );
        Toast.showSuccessMessage(
          checked ? "Módulo reativado." : "Módulo inativado."
        );
      } else {
        Toast.showErrorMessage("Não foi possível atualizar o módulo.");
      }
    } catch (error) {
      console.error(error);
      Toast.showErrorMessage("Não foi possível atualizar o módulo.");
    } finally {
      setUpdating(key, false);
    }
  };

  const columns = [
    {
      title: "Curso",
      dataIndex: "ds_titulo",
      key: "ds_titulo",
    },
    {
      title: "Início",
      dataIndex: "dt_inicio",
      key: "dt_inicio",
      width: 120,
      render: (value) => (value ? moment(value).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Conclusão",
      dataIndex: "pr_concluido",
      key: "pr_concluido",
      width: 160,
      render: (value) => (
        <Progress percent={Math.round(Number(value) || 0)} />
      ),
    },
    {
      title: "Situação",
      dataIndex: "ds_status",
      key: "ds_status",
      width: 130,
      render: (value) => (
        <Tag color={STATUS_COLORS[value] || "default"}>{value || "-"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "fg_ativo",
      key: "fg_ativo",
      width: 110,
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Ativo" : "Inativo"}</Tag>
      ),
    },
    {
      title: "Ação",
      key: "acao",
      width: 110,
      render: (_, curso) => (
        <Switch
          checked={curso.fg_ativo}
          loading={isUpdating(`curso-${curso.id}`)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
          onChange={(checked) => handleToggleEnrollment(curso, checked)}
        />
      ),
    },
  ];

  const moduleColumns = (cursoId) => [
    {
      title: "Módulo",
      dataIndex: "ds_titulo",
      key: "ds_titulo",
    },
    {
      title: "Status",
      dataIndex: "fg_ativo",
      key: "fg_ativo",
      width: 110,
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Ativo" : "Inativo"}</Tag>
      ),
    },
    {
      title: "Ação",
      key: "acao",
      width: 110,
      render: (_, modulo) => (
        <Switch
          checked={modulo.fg_ativo}
          loading={isUpdating(`modulo-${modulo.id}`)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
          onChange={(checked) => handleToggleModule(cursoId, modulo, checked)}
        />
      ),
    },
  ];

  const expandable = {
    expandedRowRender: (curso) => (
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={curso.modulos}
        columns={moduleColumns(curso.curso_id)}
      />
    ),
    rowExpandable: (curso) => curso.modulos && curso.modulos.length > 0,
  };

  return (
    <Container>
      <Title>Cursos e módulos comprados</Title>
      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={purchases}
          expandable={expandable}
          pagination={false}
          locale={{ emptyText: "Nenhum curso comprado." }}
        />
      </Spin>
    </Container>
  );
};
